/**
 * Ball physics — impulse-based bounce model.
 *
 * Implements the model from https://www.smish.dev/rocket_league/ball_simulation_1
 * exactly. The reference C++ is quoted verbatim in docs/PHYSICS_REFERENCE.md;
 * `bounce()` below is a direct transcription of it and must stay that way.
 *
 * The key property of this model — and the reason it is not ordinary rigid-body
 * collision response — is that friction opposes the SLIP velocity at the
 * contact point, `s = v_parallel + R(n x omega)`, rather than the velocity of
 * the centre of mass. That is what makes a ball dropped with backspin kick
 * backwards, which a centre-of-mass friction model cannot reproduce.
 *
 * Mass and moment of inertia cancel out of the model completely; they are
 * deliberately absent from this file.
 *
 * MUTATORS. The radius, the restitution and gravity are the three things a
 * mutator can move, so every function that reads one takes a `ResolvedPhysics`
 * (see ./mutators) and defaults to `DEFAULT_PHYSICS`, whose fields hold exactly
 * the constants this file used to import. Nothing else about the model varies.
 */

import {
  BALL_DRAG,
  BALL_FRICTION,
  BALL_FRICTION_Y,
  BALL_MAX_ANGULAR_SPEED,
  BALL_MAX_SPEED,
  BALL_RADIUS,
  BALL_SPIN_COUPLING,
} from './constants';
import { DEFAULT_PHYSICS, type ResolvedPhysics } from './mutators';
import {
  addScaledTo,
  clampLengthBy,
  clone,
  cross,
  dot,
  length,
  scale,
  sub,
  type Vec3,
  vec3,
} from './math';

export interface BallState {
  position: Vec3;
  velocity: Vec3;
  angularVelocity: Vec3;
}

export const makeBall = (
  position: Vec3 = vec3(0, 0, BALL_RADIUS),
  velocity: Vec3 = vec3(0, 0, 0),
  angularVelocity: Vec3 = vec3(0, 0, 0)
): BallState => ({
  position: clone(position),
  velocity: clone(velocity),
  angularVelocity: clone(angularVelocity),
});

/**
 * Below this slip speed the contact is treated as rolling without slipping and
 * the tangential impulse is zero. The reference expression divides by |s|, so
 * this guard is what keeps a perfectly rolling ball from producing NaN.
 */
const SLIP_EPSILON = 1e-6;

/**
 * The velocity change from a single contact with a surface of unit normal `n`.
 *
 * Returns the deltas rather than a new state so that callers handling multiple
 * simultaneous contacts (corner seams, ball wedged against the goal frame) can
 * accumulate them before applying, and so the deltas can be asserted directly
 * in tests.
 */
export const bounceDeltas = (
  velocity: Vec3,
  angularVelocity: Vec3,
  n: Vec3,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): { dv: Vec3; dw: Vec3 } => {
  const radius = physics.ballRadius;
  const vPerp = scale(n, dot(velocity, n));
  const vPara = sub(velocity, vPerp);
  const vSpin = scale(cross(n, angularVelocity), radius);
  const s = { x: vPara.x + vSpin.x, y: vPara.y + vSpin.y, z: vPara.z + vSpin.z };

  const sMag = length(s);

  // -(1 + C_R) * v_perp
  const deltaVPerp = scale(vPerp, -(1 + physics.ballRestitution));

  // -min(1, Y * |v_perp| / |s|) * mu * s
  let deltaVPara: Vec3;
  if (sMag < SLIP_EPSILON) {
    // Rolling without slipping: no slip to oppose, so no tangential impulse.
    deltaVPara = vec3(0, 0, 0);
  } else {
    const ratio = length(vPerp) / sMag;
    deltaVPara = scale(s, -Math.min(1, BALL_FRICTION_Y * ratio) * BALL_FRICTION);
  }

  return {
    dv: {
      x: deltaVPerp.x + deltaVPara.x,
      y: deltaVPerp.y + deltaVPara.y,
      z: deltaVPerp.z + deltaVPara.z,
    },
    // w + A * R * cross(delta_v_para, n) — argument order matters.
    dw: scale(cross(deltaVPara, n), BALL_SPIN_COUPLING * radius),
  };
};

/**
 * Apply a bounce off a surface with unit normal `n`, in place.
 * Position is untouched — depenetration is the collision layer's job.
 */
export const applyBounce = (
  ball: BallState,
  n: Vec3,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): void => {
  const { dv, dw } = bounceDeltas(ball.velocity, ball.angularVelocity, n, physics);
  ball.velocity.x += dv.x;
  ball.velocity.y += dv.y;
  ball.velocity.z += dv.z;
  ball.angularVelocity.x += dw.x;
  ball.angularVelocity.y += dw.y;
  ball.angularVelocity.z += dw.z;
  clampLengthBy(ball.angularVelocity, BALL_MAX_ANGULAR_SPEED);
};

/**
 * Integrate one tick of free flight: gravity, air drag, position update, and
 * the speed caps. Collision response is applied separately by the world step.
 *
 * Drag is EXPONENTIAL: `v *= (1 - DRAG)^dt`, not the linear `v *= 1 - DRAG*dt`.
 * This is Bullet's default `btRigidBody::applyDamping`; the linear form is the
 * `BT_USE_OLD_DAMPING_METHOD` branch, which RocketSim does not compile. The
 * per-tick difference is ~4.6e-6, but it compounds to ~0.05 % per second and
 * diverges badly at larger dt. Independently confirmed by rl_ball_sym
 * (`velocity *= (1. - DRAG).powf(dt)`).
 */
export const integrateBall = (
  ball: BallState,
  dt: number,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): void => {
  // Velocity first (semi-implicit Euler), then position with the new velocity.
  ball.velocity.z -= physics.gravity * dt;

  const decay = Math.pow(1 - BALL_DRAG, dt);
  ball.velocity.x *= decay;
  ball.velocity.y *= decay;
  ball.velocity.z *= decay;

  clampLengthBy(ball.velocity, BALL_MAX_SPEED);
  clampLengthBy(ball.angularVelocity, BALL_MAX_ANGULAR_SPEED);

  addScaledTo(ball.position, ball.velocity, dt);
};
